import { SelectedFilter } from '@react/Filterbar/Filters/FiltersTypes'
import { useListWrapperProps } from '@react/ListWrapper/ListWrapperTypes'
import { isAuthorMode } from '@site/js/utils/author-mode'
import { getRequest } from '@site/js/utils/request'
import { useState } from 'react'

import { LAST_ACTIVE_FILTER } from '../constants'
import {
  arrayFromSearchQueryParams,
  clearAuthorModeCache,
  getItemFromSession,
  getSearchQueryParams,
  getUrl,
  mergeAndUpdate,
  multipleSelectionHandler,
  setFiltersFromQueryParams,
  uniqueArrayElements,
  updateOffsetStates,
} from '../helpers'
import { urlParamsHandler as urlParamsHandlerUtil } from '../utils/urlParamsHandler'
import { processErrorResponse } from './timeoutHandler'

const getSessionListValue = (val: string | null): string => {
  return sessionStorage.getItem('listView') || val || 'teaser'
}

export const useListWrapper = (props: useListWrapperProps) => {
  const [notificationsChannel, setNotificationsChannel] = useState(null)
  const [translationsState, setTranslationsState] = useState(null)
  const [analyticsState, setAnalyticsState] = useState(null)
  const [selectedFilters, setSelectedFilters] = useState([])
  const [urlParams, setUrlParams] = useState(window.location.search)
  const [isFilterbarLoaded, setIsFilterbarLoaded] = useState(false)
  const [filterFieldsState, setFilterFieldsState] = useState(null)
  const [secondaryFilterFieldsState, setSecondaryFilterFieldsState] = useState(null)
  const [totalNumber, setTotalNumber] = useState(null)
  const [offsetState, setOffsetState] = useState(['0'])
  const [calculatedOffsetState, setCalculatedOffsetState] = useState(['0'])
  const [isLoading, setIsLoading] = useState(false)
  const [filterTitle, setFilterTitle] = useState('')
  const [filterTypeState, setFilterTypeState] = useState(null)
  const isLazyLoadEnabled = props?.enableLazyloadList === 'true'
  const [showLoadingSpinnerOnInit, setShowLoadingSpinnerOnInit] = useState(!isLazyLoadEnabled)
  const [itemsState, setItemsState] = useState([])
  const [responseData, setResponseData] = useState(null)
  const [listStyle, setListStyle] = useState(getSessionListValue(props.listStyle))
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)

  const prefix = `filterable${props?.listId || 0}-`

  const updateFilters = (filterFields, secondaryFilterFields, filterType) => {
    const savedPrimary = filterFieldsState || (!isAuthorMode() && getItemFromSession(`${prefix}-filterFields`))
    const savedSecondary = secondaryFilterFields || (!isAuthorMode() && getItemFromSession(`${prefix}-secondaryFilterFields`))
    
    setFilterFieldsState(
      mergeAndUpdate({
        currentData: savedPrimary,
        newData: filterFields,
        filterType,
        sessionFilterFieldsKey: `${prefix}-filterFields`,
      }),
    )
    
    setSecondaryFilterFieldsState(
      mergeAndUpdate({
        currentData: savedSecondary,
        newData: secondaryFilterFields,
        filterType,
        sessionFilterFieldsKey: `${prefix}-secondaryFilterFields`,
      }),
    )
  }

  const loadFilterableList = async (loadMoreItems = false, skipUrlFilters = false) => {
    setIsLoading(true)
    try {
      const queryParams = skipUrlFilters ? '' : multipleSelectionHandler(prefix).join('&')
      const urlConfig = {
        currentSearchQueryParams: queryParams,
        prefix,
        offset: offsetState,
        calculatedOffset: calculatedOffsetState,
        urlFromProps: props.url,
      }
      const url = getUrl(urlConfig)
      const response = await getRequest(url.replace(new RegExp(prefix, 'gi'), ''))

      if (!response.ok) {
        const handled = await processErrorResponse({
          response,
          loadMoreItems,
          itemsState,
          responseData,
          setResponseData,
          setIsLoading,
          setShowLoadingSpinnerOnInit,
        })
        if (handled) return
      }

      const data = await response.json()
      const {
        errorCode,
        filterFields,
        secondaryFilterFields,
        totalNumber,
        calculatedOffset,
        items,
        title,
        loadMore,
        dynamicFields,
        filterType,
        jobListingUrl,
        rtuExternalLinkSelectorListConfiguration,
      } = data

      if (errorCode) {
        const handled = await processErrorResponse({
          response: { ok: true, json: async () => ({ errorCode }) } as Response,
          loadMoreItems,
          itemsState,
          responseData,
          setResponseData,
          setIsLoading,
          setShowLoadingSpinnerOnInit,
        })
        if (handled) return
      }

      clearAuthorModeCache(prefix)

      if (!loadMoreItems) {
        updateFilters(filterFields, secondaryFilterFields, filterType)
      }

      setTotalNumber(totalNumber)
      updateOffsetStates({
        loadMore,
        calculatedOffset,
        itemsCount: items.length,
        offsetState,
        setOffsetState,
        setCalculatedOffsetState,
      })
      setFilterTitle(title)
      setFilterTypeState(filterType)
      setItemsState(prevState => (loadMoreItems ? [...prevState, ...items] : items))

      const responsePayload = {
        items: loadMoreItems ? [...itemsState, ...items] : items,
        filterFields: loadMoreItems ? filterFieldsState : filterFields,
        loadMore,
        totalNumber,
        dynamicFields,
        jobListingUrl,
        rtuExternalLinkSelectorListConfiguration,
      }

      setResponseData(responsePayload)
    } catch (error) {
      setResponseData('error')
      console.error(error)
    }

    setShowLoadingSpinnerOnInit(false)

    setIsLoading(false)
    sessionStorage.setItem(LAST_ACTIVE_FILTER, '')
  }

  const loadMoreData = async () => {
    await loadFilterableList(true)
  }

  const urlParamsHandler = () => {
    urlParamsHandlerUtil({
      prefix,
      offsetState,
      calculatedOffsetState,
      setOffsetState,
      setCalculatedOffsetState,
      setIsFilterbarLoaded,
      setSelectedFilters,
    })
  }

  const applyUrlFilters = async () => {
    const queryParams = arrayFromSearchQueryParams()
    if (queryParams.length > 0) {
      const selectedFilters = []
      setFiltersFromQueryParams({ queryParams, selectedFilters, prefix, setOffsetState, setCalculatedOffsetState })

      const isOffsetPresent = selectedFilters.some(filter => filter.key === 'offset')
      const isCalculatedOffsetPresent = selectedFilters.some(filter => filter.key === 'calculatedOffset')

      if (!isOffsetPresent) {
        selectedFilters.push({ key: 'offset', value: ['0'] })
      }
      if (!isCalculatedOffsetPresent) {
        selectedFilters.push({ key: 'calculatedOffset', value: ['0'] })
      }
      setIsFilterbarLoaded(true)
      setSelectedFilters(selectedFilters)
      await loadFilterableList(false, false)
    }

    if (!isInitialLoadComplete) {
      setIsInitialLoadComplete(true)
    }
  }

  const onSelectedFiltersChange = (selectedFilters: SelectedFilter[]) => {
    const searchQueryParams = []
    selectedFilters?.forEach((filter: SelectedFilter) => {
      const key = encodeURIComponent(`${prefix}${filter.key}`)
      const value = encodeURIComponent(Array.isArray(filter.value) ? filter.value.join(',') : filter.value)
      searchQueryParams.push(`${key}=${value}`)
    })

    if (isFilterbarLoaded) {
      const uniqueSearchQueryParams = uniqueArrayElements(searchQueryParams)
      const otherQueryParams = getSearchQueryParams(prefix, false)
      const hasQueryParams = uniqueSearchQueryParams.length || otherQueryParams.length
      const searchQuery = hasQueryParams ? `?${[...uniqueSearchQueryParams, ...otherQueryParams].join('&')}` : ''
      setUrlParams(searchQuery)
      setOffsetState(['0'])
      setCalculatedOffsetState(['0'])
    }
    setIsFilterbarLoaded(true)
    setSelectedFilters(selectedFilters)
  }

  return {
    analyticsState,
    filterFieldsState,
    filterTypeState,
    isLoading,
    listStyle,
    notificationsChannel,
    secondaryFilterFieldsState,
    selectedFilters,
    filterTitle,
    prefix,
    responseData,
    showLoadingSpinnerOnInit,
    totalNumber,
    translationsState,
    urlParams,
    isInitialLoadComplete,
    loadFilterableList,
    loadMoreData,
    onSelectedFiltersChange,
    setAnalyticsState,
    setListStyle,
    setNotificationsChannel,
    setTranslationsState,
    setShowLoadingSpinnerOnInit,
    urlParamsHandler,
    applyUrlFilters,
  }
}
